/**
 * CartNotification
 */

import { formatMoney } from '@shopify/theme-currency';

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.querySelector('[data-cart-notification]');
    this.items = this.querySelector('[data-cart-notification-items]');
    this.errorMessage = this.querySelector('[data-cart-notification-error-message]');
    this.errorMessageText = this.querySelector('[data-cart-notification-error-message-text]');
    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Test add to cart notification, show last added item
    // this.renderLastAddedItem();
  }

  async renderLastAddedItem() {
    const sections = this.getSectionsToRender()
      .map((section) => section.section)
      .join(',');

    // Get sections
    const response = await fetch(`/?sections=${sections}`, {
      ...fetchConfig('json', 'GET'),
    });
    const data = await response.json();

    // Show notification
    this.renderContents({
      sections: data,
    });
  }

  /**
   * handleBodyClick
   * @param {Object} event
   */
  handleBodyClick(event) {
    const target = event.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      this.close();
    }
  }

  /**
   * setActiveElement
   * @param {Node} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * open
   * @param {Boolean} hasError
   */
  open(hasError = false) {
    if (hasError) {
      this.items.classList.add('hidden');
      this.errorMessage.classList.remove('hidden');
    } else {
      this.items.classList.remove('hidden');
      this.errorMessage.classList.add('hidden');
    }

    document.querySelector('body').classList.add('cart-notification-is-open');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );
  }

  /**
   * close
   */
  close() {
    document.querySelector('body').classList.remove('cart-notification-is-open');
    removeTrapFocus(this.activeElement);
  }

  /**
   * renderContents
   * @param {Object} parsedState
   */
  renderContents(parsedState, quantityUpdate = false) {
    this.getSectionsToRender().forEach((section) => {
      // Get HTML and parse it
      let html = document.createElement('div');
      const content = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
      html.innerHTML = content;

      // On quantity update don't rerender the cart notification items button update the quantity state
      if (section.id == 'cart-notification-items' && quantityUpdate) {
        this.updateLineId();
        this.updateQuantityState();
        return;
      }

      // Render cart notification upsells only the first time
      if (section.id == 'cart-notification-upsell' && quantityUpdate) {
        return;
      }

      // Update all other sections
      document.getElementById(section.id).innerHTML = content;

      // Update the quantity state
      if (section.id == 'cart-notification-items') {
        this.updateQuantityState();
      }
    });
    this.renderTotals();
    this.open();
  }

  /**
   * Render the total of products in the cart notification
   */
  renderTotals() {
    // Variables to hold the total price
    let price = 0;
    let compareAtPrice = 0;

    // Loop through all items and add the price to the total
    this.querySelector('[data-cart-notification-items]')
      .querySelectorAll('[data-cart-item]')
      .forEach((el, index) => {
        const itemPrice = el.querySelector('[data-item-price]').dataset.itemPrice || 0;
        const itemCompareAtPrice =
          el.querySelector('[data-item-compare-at-price]').dataset.itemCompareAtPrice || 0;
        const itemQuantity = parseInt(el.querySelector('quantity-input input').value) || 1;
        price += itemPrice * itemQuantity;
        compareAtPrice += itemCompareAtPrice * itemQuantity;
      });

    // Show compare at price if it's higher than the price
    const showCompareAtPrice = compareAtPrice > price;

    // formatted price
    const formattedPrice =
      price == 0 ? window.cartStrings.free : formatMoney(price, window.moneyFormat);
    const formattedCompareAtPrice =
      price == 0 ? window.cartStrings.free : formatMoney(compareAtPrice, window.moneyFormat);

    // Render the total price
    this.querySelector('[data-cart-total-price]').innerHTML = `
      <span class="money">${formattedPrice}</span>
      <s class="price--compare ml-1${
        !showCompareAtPrice && ` hidden`
      }">${formattedCompareAtPrice}</s>
    `;
  }

  /**
   * renderError
   * @param {String} message - error message
   * @param {String} inventoryQuantity - inventory quantity
   */
  renderError(message, inventoryQuantity = null) {
    const errorMessageElem = this.notification.querySelector(
      '[data-cart-notification-error-message-text]'
    );

    if (inventoryQuantity) {
      errorMessageElem.innerText = window.cartStrings.quantityError.replace(
        '[quantity]',
        inventoryQuantity
      );
    }

    errorMessageElem.innerText = message;
    errorMessageElem.classList.remove('hidden');

    this.renderTotals();
    this.open(true);
  }

  /**
   * Update the line id for the cart notification items that are not fully replaced
   * Needed because the line key can be updated based on cart changes from different cart lines
   */
  async updateLineId() {
    const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
      ...fetchConfig('json', 'GET'),
    });
    const cartData = await cartResponse.json();
    this.querySelector('[data-cart-notification-items]')
      .querySelectorAll('[data-cart-item]')
      .forEach((el) => {
        const line_key = el.dataset?.cartItem;
        const line_id = line_key.split(':')[0];
        const item = cartData.items.find((item) => item.id == line_id);
        if (item) {
          el.dataset.cartItem = item.key;
          el.querySelector('[data-cart-item-quantity]').dataset.cartItemQuantity = item.key;
          el.querySelector('[data-cart-item-quantity]').dataset.index = item.key;
          el.querySelector('cart-remove-button').dataset.index = item.key;
          el.querySelector('[data-cart-item-error]').dataset.cartItemPrice = item.key;
        }
      });
  }

  /**
   * Update the line item quantity state
   */
  updateQuantityState() {
    this.querySelector('[data-cart-notification-items]')
      .querySelectorAll('[data-cart-item]')
      .forEach((el, index) => {
        const productType = el.dataset?.productType;
        const productId = el.dataset?.productId;
        const quantityEl = el.querySelector('quantity-input');
        const quantity = quantityEl.querySelector('input').value;
        if (quantity <= 0) {
          el.remove();
          this.querySelector(`upsell-product[data-ref-product-id="${productId}"]`).toggleVisible(
            true
          );
        }
      });
  }

  /**
   * Add line item to cart notification (to show upsell product)
   * @param {Object} parsedState
   */
  addLineItem(parsedState) {
    if (!parsedState) {
      return;
    }

    document
      .querySelector('[data-cart-notification-items]')
      .insertAdjacentHTML(
        'beforeend',
        this.getSectionInnerHTML(
          parsedState.sections['cart-notification-items'],
          '#cart-notification-items'
        )
      );
  }

  /**
   * getSectionsToRender
   * @returns {Array} Sections to render
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-button',
        section: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
      {
        id: 'cart-notification-items',
        section: 'cart-notification-items',
        selector: `#cart-notification-items`,
      },
      {
        id: 'cart-notification-upsell',
        section: 'cart-notification-upsell',
        selector: `#cart-notification-upsell`,
      },
    ];
  }

  /**
   * getSectionInnerHTML
   * @param {String} String that contains html
   * @param {String} selector
   * @returns
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
